<script setup>
import { ref, watch, computed } from 'vue'
import api from '@/api'
import Swal from 'sweetalert2'
import { useRouter } from 'vue-router'
import { encodeId } from '@/utils/obfuscate'

// Props
const props = defineProps({
    open: { type: Boolean, default: false },
    userId: { type: Number, default: null }
})
const emit = defineEmits(['close'])
const router = useRouter()
const avatarError = ref(false)

// State
const loading = ref(false)
const user = ref(null)
const items = ref([])            // últimas membresías con saldo
const errorMsg = ref('')

// helpers
const fCurrency = (n) =>
    new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD' })
        .format(Number(n || 0))

const estadoClass = (s) => ({
    ACTIVA: 'bg-success',
    activa: 'bg-success',
    pendiente: 'bg-secondary',
    programada: 'bg-info',
    vencida: 'bg-dark',
    cancelada: 'bg-danger',
})[String(s || '').toLowerCase()] || 'bg-light text-dark'

const actives = computed(() => items.value.filter(i => i.estado === 'activa').length)
const pendings = computed(() => items.value.filter(i => i.estado === 'pendiente').length)
const scheduled = computed(() => items.value.filter(i => i.estado === 'programada').length)
const totalSaldo = computed(() => items.value.reduce((a, b) => a + Number(b.saldo_pendiente || 0), 0))

function onAvatarError() { avatarError.value = true }
const iniciales = computed(() => {
    const n = user.value?.nombre?.trim() || ''
    if (!n) return '—'
    const parts = n.split(/\s+/).slice(0, 2)
    return parts.map(p => p[0]?.toUpperCase() || '').join('') || '—'
})
// Carga — FE-022: usa resumen financiero ya incluido por backend, evita N requests /saldo
async function loadPeek() {
    if (!props.userId) return
    loading.value = true
    errorMsg.value = ''
    user.value = null
    items.value = []
    try {
        // 1) Socio
        const uRes = await api.get(`/users/${props.userId}`)
        user.value = uRes.data

        // 2) Últimas 5 membresías del usuario — backend ya incluye saldo_pendiente/precio_unitario via financial.service
        const mRes = await api.get(`/membresia-usuarios/usuario/${props.userId}`, {
            params: { limit: 5 }
        })
        const rows = mRes.data || []

        items.value = rows.map((r) => ({
            id: r.id,
            nombre: r?.membresia?.nombre || '—',
            fecha_inicio: r.fecha_inicio,
            fecha_fin: r.fecha_fin,
            estado: r.estado,
            precio_unitario: Number(r.precio_unitario ?? r?.membresia?.precio ?? 0),
            saldo_pendiente: Number(r.saldo_pendiente ?? r.saldo ?? 0),
        }))
    } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.error(e)
        errorMsg.value = e?.response?.data?.message || 'No se pudo cargar la vista rápida'
        Swal.fire('Error', errorMsg.value, 'error')
    } finally {
        loading.value = false
    }
}

// Abrir / cerrar
watch(
    () => props.open,
    (val) => { if (val) loadPeek() }
)

function close() { emit('close') }
function goDetalle() {
    if (!user.value) return
    router.push({ name: 'SocioDetalle', params: { id: encodeId(user.value.id) } })
}
</script>

<template>
    <!-- overlay -->
    <transition name="fade">
        <div v-if="open" class="overlay" @click.self="close"></div>
    </transition>

    <!-- drawer -->
    <transition name="slide-right">
        <aside v-if="open" class="drawer">
            <header class="d-flex align-items-center justify-content-between mb-2">
                <h6 class="mb-0">Vista rápida</h6>
                <button class="btn btn-sm btn-outline-secondary" @click="close">
                    <i class="ni ni-fat-remove"></i>
                </button>
            </header>

            <div v-if="loading" class="py-4 text-center text-muted">Cargando…</div>

            <template v-else>
                <div class="mb-3 d-flex gap-3 align-items-center">
                    <!-- Avatar con fallback -->
                    <div class="avatar-wrapper">
                        <img v-if="user?.avatar_url && !avatarError" :src="user.avatar_url" alt="Avatar"
                            class="avatar-img" loading="lazy" decoding="async" @error="onAvatarError" />
                        <div v-else class="avatar-fallback">{{ iniciales }}</div>
                    </div>

                    <!-- Datos del socio -->
                    <div>
                        <div class="fw-bold">{{ user?.nombre || '—' }}</div>
                        <small class="text-muted">ID: {{ user?.id }} • Rol: {{ user?.rol?.nombre || '—' }}</small>
                        <div class="small text-muted mt-1">
                            Identificación: {{ user?.identificacion || '—' }}<br>
                            Email: {{ user?.email || '—' }}<br>
                            Teléfono: {{ user?.telefono || '—' }}
                        </div>
                    </div>
                </div>


                <div class="card p-3 mb-3">
                    <div class="d-flex align-items-center justify-content-between">
                        <div>
                            <div class="small text-muted">Activas</div>
                            <div class="fs-5">{{ actives }}</div>
                        </div>
                        <div>
                            <div class="small text-muted">Pendientes</div>
                            <div class="fs-5">{{ pendings }}</div>
                        </div>
                        <div>
                            <div class="small text-muted">Programadas</div>
                            <div class="fs-5">{{ scheduled }}</div>
                        </div>
                        <div>
                            <div class="small text-muted">Saldo total</div>
                            <div class="fs-5">{{ fCurrency(totalSaldo) }}</div>
                        </div>
                    </div>
                </div>

                <div class="mb-2 d-flex align-items-center justify-content-between">
                    <div class="fw-semibold">Membresías recientes</div>
                    <button class="btn btn-sm btn-primary" @click="goDetalle">
                        Ver detalle
                    </button>
                </div>

                <div v-if="items.length === 0" class="text-muted small">Sin registros.</div>

                <ul class="list-unstyled mb-0">
                    <li v-for="it in items" :key="it.id" class="mb-2 card p-2">
                        <div class="d-flex justify-content-between align-items-start">
                            <div>
                                <div class="fw-semibold">{{ it.nombre }}</div>
                                <small class="text-muted">
                                    {{ it.fecha_inicio }} → {{ it.fecha_fin }}
                                </small>
                            </div>
                            <span class="badge" :class="estadoClass(it.estado)">{{ it.estado?.toUpperCase() }}</span>
                        </div>

                        <div class="d-flex justify-content-between mt-2 small">
                            <div>Precio: <strong>{{ fCurrency(it.precio_unitario) }}</strong></div>
                            <div>Saldo: <strong
                                    :class="{ 'text-success': it.saldo_pendiente == 0, 'text-danger': it.saldo_pendiente > 0 }">
                                    {{ fCurrency(it.saldo_pendiente) }}
                                </strong></div>
                        </div>
                    </li>
                </ul>
            </template>
        </aside>
    </transition>
</template>

<style scoped>
/* overlay */
.overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, .25);
    z-index: 1030;
}

/* drawer */
.drawer {
    position: fixed;
    inset: 0 0 0 auto;
    width: 420px;
    max-width: 90vw;
    background: #fff;
    z-index: 1040;
    box-shadow: -2px 0 18px rgba(0, 0, 0, .15);
    padding: 16px 16px 24px 16px;
    overflow: auto;
    border-right: 1px solid #eee;
}

/* transitions */
.slide-right-enter-active,
.slide-right-leave-active {
    transition: transform .22s ease;
}

.slide-right-enter-from,
.slide-right-leave-to {
    transform: translateX(100%);
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.slide-left-enter-active,
.slide-left-leave-active {
    transition: transform .22s ease;
}

.slide-left-enter-from,
.slide-left-leave-to {
    transform: translateX(-100%);
}

.avatar-wrapper {
    width: 64px;
    height: 64px;
    border-radius: 12px;
    overflow: hidden;
    background: #f0f2f5;
    display: grid;
    place-items: center;
    box-shadow: 0 0 6px rgba(0, 0, 0, .08);
    flex: 0 0 auto;
}

.avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.avatar-fallback {
    width: 100%;
    height: 100%;
    display: grid;
    place-items: center;
    font-weight: 600;
    letter-spacing: .5px;
    color: #445;
    background: linear-gradient(180deg, #eef1f6, #e7ebf2);
    user-select: none;
}
</style>
